import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["Features", "/p/features"],
  ["AI Generator", "/p/ai-document-generator"],
  ["Industries", "/p/industry-solutions"],
  ["How it works", "/p/how-it-works"],
  ["Pricing", "/pricing"],
];

const FOOTER = {
  Product: [["Features", "/p/features"], ["AI Document Generator", "/p/ai-document-generator"], ["RAMS Generator", "/p/rams-generator"], ["Risk Assessments", "/p/risk-assessment-generator"], ["Method Statements", "/p/method-statement-generator"], ["COSHH Generator", "/p/coshh-generator"], ["Pricing", "/pricing"]],
  Solutions: [["CDM Management", "/p/cdm-management"], ["Toolbox Talks", "/p/toolbox-talks"], ["HSE Policies", "/p/hse-policies"], ["Fire Safety", "/p/fire-safety"], ["Permit to Work", "/p/permit-to-work"], ["Health & Safety Forms", "/p/health-safety-forms"], ["Industry Solutions", "/p/industry-solutions"]],
  Platform: [["Training Management", "/p/training-management"], ["Compliance Management", "/p/compliance-management"], ["Workforce Management", "/p/workforce-management"], ["How It Works", "/p/how-it-works"], ["Help Centre", "/p/help-centre"], ["Knowledge Base", "/p/knowledge-base"], ["Blog & Resources", "/p/blog"]],
  Company: [["About Saflr", "/p/about"], ["Contact", "/contact"], ["Privacy Policy", "/legal/privacy-policy"], ["Cookie Policy", "/legal/cookie-policy"], ["Terms of Service", "/legal/terms-of-service"], ["Acceptable Use", "/legal/acceptable-use-policy"], ["Subscription Terms", "/legal/subscription-terms"], ["AI Content Disclaimer", "/legal/ai-disclaimer"]],
};

const Nav = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  const dest = user ? (user.is_super_admin ? "/admin" : "/app") : "/register";
  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled ? "border-b border-white/10 bg-navy/80 backdrop-blur-xl" : "bg-transparent"}`}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Logo light />
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map(([l, h]) => (
            <NavLink key={h} to={h} data-testid={`nav-${l.toLowerCase().replace(/\s/g, "-")}`} className={({ isActive }) => `rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "text-white" : "text-slate-400 hover:text-white"}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-2 lg:flex">
          {!user && <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-sm text-slate-300 hover:text-white">Log in</Link>}
          <Link to={dest} data-testid="nav-start-creating" className="group inline-flex items-center gap-1.5 rounded-full bg-health px-4 py-2 text-sm font-semibold text-navy transition-transform hover:-translate-y-0.5">
            {user ? "Open Saflr" : "Start Creating"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        <button className="text-white lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-navy px-5 pb-6 lg:hidden">
          {NAV.map(([l, h]) => <Link key={h} to={h} onClick={() => setOpen(false)} className="block py-3 text-slate-200">{l}</Link>)}
          <Link to="/login" onClick={() => setOpen(false)} className="block py-3 text-slate-200">Log in</Link>
          <Link to={dest} onClick={() => setOpen(false)} className="mt-2 block rounded-full bg-health py-3 text-center font-semibold text-navy">Start Creating</Link>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-white/10 bg-navy text-slate-400">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-6 lg:px-8">
      <div className="lg:col-span-2">
        <Logo light />
        <p className="mt-4 max-w-xs text-sm">Safety. Simplified. AI-assisted Health, Safety &amp; Environment management for UK businesses.</p>
        <p className="mt-6 max-w-xs text-xs leading-relaxed text-slate-500">AI-generated content is a draft and must be reviewed and approved by a competent person. Saflr does not certify legal compliance.</p>
      </div>
      {Object.entries(FOOTER).map(([h, links]) => (
        <div key={h}>
          <p className="eyebrow mb-4 text-slate-300">{h}</p>
          <ul className="space-y-2.5 text-sm">
            {links.map(([l, to]) => <li key={to}><Link to={to} className="transition-colors hover:text-white">{l}</Link></li>)}
          </ul>
        </div>
      ))}
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500">© {new Date().getFullYear()} Saflr. All rights reserved.</div>
  </footer>
);

export default function MarketingLayout() {
  return (
    <div className="dark min-h-screen bg-navy text-slate-100">
      <Nav />
      <Outlet />
      <Footer />
    </div>
  );
}
